import { isAxiosError } from 'axios'

export interface AuthFlowParams {
  lang?: string
  country?: string
  plan?: string
  redirect?: string
  website?: string
  reauthenticate?: boolean
}

const CONTEXT_FIELDS = ['lang', 'country', 'plan', 'redirect', 'website'] as const

export function safeAuthRedirect(value: unknown): string {
  return typeof value === 'string' && /^\/(?!\/)/.test(value) && !/[\\\x00-\x1f\x7f]/.test(value)
    && !/^\/auth(?:\/|\?|#|$)/.test(value) ? value : ''
}

export function rememberAuthContext(params: Partial<Record<typeof CONTEXT_FIELDS[number], unknown>>): void {
  for (const key of CONTEXT_FIELDS) {
    const value = key === 'redirect' ? safeAuthRedirect(params[key]) : params[key]
    if (typeof value === 'string' && value) sessionStorage.setItem(`auth_${key}`, value)
  }
}

export function readAuthContext(): AuthFlowParams {
  const context: AuthFlowParams = {}
  for (const key of CONTEXT_FIELDS) {
    const stored = sessionStorage.getItem(`auth_${key}`)
    const value = key === 'redirect' ? safeAuthRedirect(stored) : stored
    if (value) context[key] = value
  }
  return context
}

// The word the address bar shows for a failed sign-in (`/auth?error=<word>`),
// the same words the backend's callback uses; the sign-in page turns each
// into "what to do". Internal codes never reach the URL.
export function authErrorWord(code: string | null | undefined): string {
  if (code === 'EMAIL_VERIFICATION_REQUIRED') return 'email-unverified'
  if (code === 'ACCOUNT_LINK_REQUIRED') return 'original-sign-in'
  if (code === 'AUTHENTICATION_UNAVAILABLE') return 'sign-in-unavailable'
  return 'failed'
}

export function authFailureCode(error: unknown): string {
  if (isAxiosError(error)) {
    const code = error.response?.data?.code
    if (['EMAIL_VERIFICATION_REQUIRED', 'ACCOUNT_LINK_REQUIRED', 'AUTHENTICATION_UNAVAILABLE'].includes(code)) return code
    if (error.response?.status === 401) return 'AUTHENTICATION_REQUIRED'
  }
  return 'AUTHENTICATION_UNAVAILABLE'
}
